// shop-frontend/src/utils/api.js
/* =========================================================
   ✅ SHOP FRONTEND API (matches backend/app.js mounts)
   - /api/shop/auth
   - /api/shop/products
   - /api/shop/orders
   - /api/shop/earnings
   - /api/shop/feed
   - /api/shop-mall (builder /me + cover)
   - /api/public/shops/:shopId/mall (public preview)
   - /api/notifications
   - /api/messages
   - /api/reviews
   - /api (industries)
   ========================================================= */

const PROD_BASE = "https://moondala-one.onrender.com";

/* =========================
   Base URL resolution (single source of truth)
   Priority: REACT_APP_API_BASE > REACT_APP_BACKEND_URL > localhost auto-detect > PROD_BASE
   See .env.example for the canonical list of env vars.
   ========================= */
function resolveBase() {
  // 1. Explicit CRA env var (preferred — set in .env)
  if (typeof process !== "undefined" && process.env) {
    const explicit =
      process.env.REACT_APP_API_BASE ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_SERVER_URL ||
      process.env.REACT_APP_API_URL;
    if (explicit) return explicit;
  }

  // 2. Auto-detect local dev (browser on localhost → backend on :5000)
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:5000";
    }
  }

  // 3. Non-production Node env (SSR edge case)
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    return "http://localhost:5000";
  }

  return PROD_BASE;
}

function normalizeBase(u) {
  const s = String(u || "").trim();
  if (!s) return PROD_BASE;
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

export const API_BASE = normalizeBase(resolveBase());

// ✅ Always point API_ROOT to ".../api"
export const API_ROOT = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;

// ASSET_BASE: backend root used for static assets (strip trailing `/api` if present)
export const ASSET_BASE = normalizeBase(API_BASE).replace(/\/api$/i, "");

/* =========================
   Image URL Helper
   ========================= */
/**
 * Convert relative image paths to absolute URLs
 * Handles uploads from backend (/uploads/...) and fixes localhost URLs
 */
export function toAbsUrl(url) {
  if (!url) return "";
  let s = String(url).trim();

  if (!s || s === "[object Object]") return "";
  if ((s.startsWith("[") && s.endsWith("]")) || (s.startsWith("{") && s.endsWith("}"))) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          const out = toAbsUrl(item);
          if (out) return out;
        }
        return "";
      }
      if (parsed && typeof parsed === "object") {
        return toAbsUrl(parsed.url || parsed.secure_url || parsed.path || parsed.image || parsed.src || "");
      }
    } catch {
      // keep original string if JSON parse fails
    }
  }

  s = s.replace(/^['"]+|['"]+$/g, "").trim();
  if (!s) return "";

  // Normalize Windows-style separators and accidental local file-style prefixes
  s = s.replace(/\\+/g, "/");
  s = s.replace(/^file:\/\//i, "");
  s = s.replace(/^\/?[A-Za-z]:\//, "");
  s = s.replace(/^\/?fakepath\//i, "");
  s = s.replace(/^\/?uploads\//i, "uploads/");
  s = s.replace(/^\/+/, "/");

  // 1. If it's already a valid Cloudinary URL, return as is
  if (s.includes("res.cloudinary.com")) return s;

  // 2. Fix localhost URLs (Force HTTPS in production to avoid mixed content)
  if (s.includes("localhost:5000") || s.includes("localhost:3000")) {
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return s.replace(/http:\/\/localhost:\d+/g, "https://moondala-one.onrender.com");
    }
    return s.replace(/http:\/\/localhost:\d+/g, ASSET_BASE);
  }
  if (s.includes("127.0.0.1:5000")) return s.replace(/http:\/\/127\.0\.0\.1:5000/g, ASSET_BASE);

  // 3. ROBUST CLOUDINARY DETECTION: Extract cloud name and path regardless of prefixes
  // Matches: .../cloudname/image/upload/...
  const cloudMatch = s.match(/(?:^|\/)([a-z0-9_-]+)\/(image|video)\/upload\/(.+)/i);
  if (cloudMatch) {
    const cloudName = cloudMatch[1];
    const type = cloudMatch[2].toLowerCase(); // image or video
    const rest = cloudMatch[3]; // v123/folder/file.jpg
    
    // "uploads" is likely a folder prefix, not a cloud name, unless it's the actual cloud name
    if (cloudName !== 'uploads') {
       return `https://res.cloudinary.com/${cloudName}/${type}/upload/${rest}`;
    }
  }

  // 4. Already absolute URL (and not Cloudinary, since we checked above)
  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  // 5. Cloudinary check fallback (if regex missed but structure looks like Cloudinary)
  if (s.includes("/image/upload/") || s.includes("/video/upload/")) {
     // Try to clean up path blindly
     let clean = s;
     if (clean.startsWith("/uploads/")) clean = clean.replace("/uploads/", "/");
     if (clean.startsWith("uploads/")) clean = clean.replace("uploads/", "/");
     if (!clean.startsWith("/")) clean = "/" + clean;
     return `https://res.cloudinary.com${clean}`;
  }

  // 6. ✅ NEW: Detect Cloudinary folder paths like "moondala/products/abc123.jpg"
  // These are stored by Cloudinary uploads without the full URL structure
  if (s.startsWith("moondala/") && !s.startsWith("/") && !s.startsWith("http")) {
    const lower = s.toLowerCase();
    const isVideo = lower.endsWith(".mp4") || lower.endsWith(".mov") || lower.endsWith(".webm") || lower.includes("/video/");
    const kind = isVideo ? "video" : "image";
    return `https://res.cloudinary.com/dohetomaw/${kind}/upload/${s}`;
  }

  // 6.5 Plain filename fallback => assume backend uploads folder
  // Example: "abc123.jpg" should resolve to "/uploads/abc123.jpg"
  if (!s.includes("/") && /\.[a-z0-9]{2,6}$/i.test(s)) {
    return `${ASSET_BASE}/uploads/${s}`;
  }
  
  // 7. Relative path - add ASSET_BASE (never /api for images)
  if (s.startsWith("/")) return `${ASSET_BASE}${s}`;
  return `${ASSET_BASE}/${s}`;
}

function apiUrl(path) {
  const p = String(path || "").trim();
  if (!p) return API_ROOT;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;

  // Cache-bust endpoints where stale data causes visible UX issues
  // (e.g. mall preview shows old layout after save, notification badge stuck)
  const needsCacheBust = p.includes("/shop-mall/") || p.includes("/mall/") || p.includes("/notifications") || p.includes("/orders");
  const separator = p.includes("?") ? "&" : "?";
  
  // Use more aggressive cache busting for production (add random component)
  const cacheBust = needsCacheBust ? `${separator}_t=${Date.now()}&_r=${Math.random().toString(36).substring(7)}` : "";

  // already includes /api
  if (p.startsWith("/api/")) return `${API_BASE}${p}${cacheBust}`;

  // begins with /
  if (p.startsWith("/")) return `${API_ROOT}${p}${cacheBust}`;

  return `${API_ROOT}/${p}${cacheBust}`;
}

/* =========================
   Shop session helpers
   ========================= */
const SHOP_TOKEN_KEY = "shopToken";
const SHOP_ID_KEY = "shopId"; // ✅ should store Mongo _id (24 hex)

export function setShopSession({ token, shopId } = {}) {
  try {
    if (token) localStorage.setItem(SHOP_TOKEN_KEY, String(token));
    if (shopId) localStorage.setItem(SHOP_ID_KEY, String(shopId));
  } catch {}
}

export function getShopToken() {
  try {
    return localStorage.getItem(SHOP_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function getShopId() {
  try {
    return localStorage.getItem(SHOP_ID_KEY) || "";
  } catch {
    return "";
  }
}

export function clearShopSession() {
  try {
    localStorage.removeItem(SHOP_TOKEN_KEY);
    localStorage.removeItem(SHOP_ID_KEY);
  } catch {}
}

/* =========================
   Generic request wrapper
   ========================= */
export async function request(
  path,
  { method = "GET", body, headers = {}, auth = true } = {}
) {
  const h = { Accept: "application/json", ...headers };

  const isForm = typeof FormData !== "undefined" && body instanceof FormData;

  let payload = body;
  if (body !== undefined && body !== null && !isForm && typeof body === "object") {
    h["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  // ✅ backend allows Authorization Bearer
  if (auth) {
    const token = getShopToken();
    if (token) h.Authorization = `Bearer ${token}`;
  }

  // ✅ Force no-cache for mall endpoints to prevent stale preview after save
  const isMallEndpoint = path.includes("/shop-mall/") || path.includes("/mall/");
  
  // Add extra headers for production cache bypass
  if (isMallEndpoint) {
    h["Cache-Control"] = "no-store, no-cache, must-revalidate, proxy-revalidate";
    h["Pragma"] = "no-cache";
    h["Expires"] = "0";
  }
  
  const fetchOptions = {
    method,
    headers: h,
    body: method === "GET" || method === "HEAD" ? undefined : payload,
  };
  
  // Add cache control for mall endpoints
  if (isMallEndpoint && method === "GET") {
    fetchOptions.cache = "no-store";
  }

  const res = await fetch(apiUrl(path), fetchOptions);

  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");

  let data = null;
  try {
    data = isJson ? await res.json() : await res.text();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg =
      (data && data.message) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    err.path = path;
    throw err;
  }

  return data;
}

export function apiGet(path, opts = {}) {
  return request(path, { ...opts, method: "GET" });
}
export function apiPost(path, body, opts = {}) {
  return request(path, { ...opts, method: "POST", body });
}
export function apiPut(path, body, opts = {}) {
  return request(path, { ...opts, method: "PUT", body });
}
export function apiPatch(path, body, opts = {}) {
  return request(path, { ...opts, method: "PATCH", body });
}
export function apiDelete(path, opts = {}) {
  return request(path, { ...opts, method: "DELETE" });
}

/* =========================
   Try multiple paths (fallback on 404)
   Re-throws non-404 errors immediately.
   ========================= */
export async function tryPaths(fnList) {
  let lastErr = null;
  for (const fn of fnList) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (e && Number(e.status) !== 404) throw e;
    }
  }
  throw lastErr || new Error("All endpoints failed");
}

/* =========================
   Helpers used by components
   ========================= */
export function pickConversationId(c) {
  if (!c) return "";
  return String(c._id || c.id || c.conversationId || c.threadId || "").trim();
}

/* =========================
   ✅ SHOP AUTH
   ========================= */
export async function shopLogin(email, password) {
  const data = await tryPaths([
    () => apiPost("/shop/auth/login", { email, password }, { auth: false }),
    () => apiPost("/shops/auth/login", { email, password }, { auth: false }),
    // optional legacy:
    () => apiPost("/shop/login", { email, password }, { auth: false }),
    () => apiPost("/shops/login", { email, password }, { auth: false }),
  ]);

  const token = data?.token || data?.shopToken || data?.data?.token || "";

  // ✅ prefer Mongo _id first (needed for preview/public routes)
  const shopId =
    data?.shop?._id ||
    data?.shop?.id ||
    data?.data?.shop?._id ||
    data?.data?.shop?.id ||
    data?.shopId ||
    data?.data?.shopId ||
    "";

  if (token) setShopSession({ token, shopId });
  return data;
}

export async function shopRegister(payload) {
  const data = await tryPaths([
    () => apiPost("/shop/auth/register", payload, { auth: false }),
    () => apiPost("/shops/auth/register", payload, { auth: false }),
    // optional legacy:
    () => apiPost("/shop/register", payload, { auth: false }),
    () => apiPost("/shops/register", payload, { auth: false }),
  ]);

  const token = data?.token || data?.shopToken || data?.data?.token || "";

  const shopId =
    data?.shop?._id ||
    data?.shop?.id ||
    data?.data?.shop?._id ||
    data?.data?.shop?.id ||
    data?.shopId ||
    data?.data?.shopId ||
    "";

  if (token) setShopSession({ token, shopId });
  return data;
}

export function shopLogout() {
  clearShopSession();
}

/* =========================
   ✅ Shop profile "me"
   ========================= */
export async function getShopMe() {
  return tryPaths([
    () => apiGet("/shop/auth/me"),
    () => apiGet("/shops/auth/me"),
    // optional legacy:
    () => apiGet("/shop/me"),
    () => apiGet("/shops/me"),
    () => apiGet("/shop/profile"),
    () => apiGet("/shops/profile"),
  ]);
}

// (keep old name if other files import it)
export async function getShopMallMe() {
  return getShopMe();
}

/* =========================
   ✅ INDUSTRIES (public)
   ========================= */
export async function getIndustries() {
  return tryPaths([
    () => apiGet("/public/industries", { auth: false }),
    () => apiGet("/industries", { auth: false }),
    () => apiGet("/industry", { auth: false }),
    () => apiGet("/industries/all", { auth: false }),
  ]);
}

export async function getCategoriesForIndustry(industryId) {
  if (!industryId) throw new Error("industryId is required");
  return tryPaths([
    () => apiGet(`/public/industries/${industryId}/categories`, { auth: false }),
    () => apiGet(`/industries/${industryId}/categories`, { auth: false }),
  ]);
}

/* =========================
   ✅ SHOP PRODUCTS
   ========================= */
export async function getMyShopProducts(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return tryPaths([
    () => apiGet(`/shop/products${suffix}`),
    () => apiGet(`/shops/products${suffix}`),
    // optional legacy:
    () => apiGet(`/shop/products/mine${suffix}`),
    () => apiGet(`/shops/products/mine${suffix}`),
  ]);
}

export async function getShopPublishReadiness() {
  return tryPaths([
    () => apiGet("/shop/products/publish-readiness"),
    () => apiGet("/shops/products/publish-readiness"),
  ]);
}

export async function deleteShopProduct(productId) {
  if (!productId) throw new Error("productId is required");
  return tryPaths([
    () => apiDelete(`/shop/products/${productId}`),
    () => apiDelete(`/shops/products/${productId}`),
    // optional legacy:
    () => apiDelete(`/shop/products/delete/${productId}`),
  ]);
}

/* =========================
   ✅ SHOP ORDERS
   ========================= */
export async function getShopOrders(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return tryPaths([
    () => apiGet(`/shop/orders${suffix}`),
    () => apiGet(`/shops/orders${suffix}`),
    // optional legacy:
    () => apiGet(`/shop/orders/list${suffix}`),
  ]);
}

export async function updateShopOrderStatus(orderId, status) {
  if (!orderId) throw new Error("orderId is required");
  return tryPaths([
    () => apiPut(`/shop/orders/${orderId}/status`, { status }),
    () => apiPut(`/shops/orders/${orderId}/status`, { status }),
    // optional patch legacy:
    () => apiPatch(`/shop/orders/${orderId}/status`, { status }),
  ]);
}

/* =========================
   ✅ SHOP EARNINGS
   ========================= */
export async function getShopEarnings() {
  return tryPaths([
    () => apiGet("/shop/earnings"),
    () => apiGet("/shops/earnings"),
    // optional legacy:
    () => apiGet("/shop/earnings/summary"),
  ]);
}

/* =========================
   ✅ SHOP RETURNS
   ========================= */
export async function getShopReturns(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return tryPaths([
    () => apiGet(`/shop/returns${suffix}`),
    () => apiGet(`/shops/returns${suffix}`),
    // optional legacy:
    () => apiGet(`/shop/returns/list${suffix}`),
  ]);
}

export async function getShopCartWatchers() {
  return tryPaths([
    () => apiGet("/shop/cart-watchers"),
    () => apiGet("/shops/cart-watchers"),
  ]);
}

export async function createShopReturnLabel(returnId) {
  if (!returnId) throw new Error("returnId is required");
  return tryPaths([
    () => apiPost(`/shop/returns/${returnId}/label`, {}),
    () => apiPost(`/shops/returns/${returnId}/label`, {}),
  ]);
}

export async function updateShopReturnStatus(returnId, status, shopNote) {
  if (!returnId) throw new Error("returnId is required");
  const body = { status };
  if (typeof shopNote === "string" && shopNote.trim()) body.shopNote = shopNote.trim();
  return tryPaths([
    () => apiPut(`/shop/returns/${returnId}/status`, body),
    () => apiPut(`/shops/returns/${returnId}/status`, body),
    // optional legacy:
    () => apiPut(`/shop/returns/${returnId}/update-status`, body),
  ]);
}

/* =========================
   ✅ SHOP CUSTOMERS & FOLLOWERS
   ========================= */
export async function getShopCustomersAndFollowers() {
  return tryPaths([
    () => apiGet("/shop/customers"),
    () => apiGet("/shops/customers"),
  ]);
}

/* =========================
   ✅ SHOP FEED
   ========================= */
export async function getShopFeed(params = {}) {
  const qs = new URLSearchParams();
  // Posts only (product cards used to crowd posts out of the window), paged
  // with { before: <nextBefore from the previous page> }.
  Object.entries({ postsOnly: 1, ...(params || {}) }).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return tryPaths([
    () => apiGet(`/shop/feed${suffix}`),
    () => apiGet(`/shops/feed${suffix}`),
    // optional legacy:
    () => apiGet(`/shop/posts${suffix}`),
    () => apiGet(`/shops/posts${suffix}`),
  ]);
}

export async function createShopPost(formDataOrBody) {
  const isForm = typeof FormData !== "undefined" && formDataOrBody instanceof FormData;

  return tryPaths([
    () =>
      isForm
        ? request("/shop/feed", { method: "POST", body: formDataOrBody, auth: true })
        : apiPost("/shop/feed", formDataOrBody || {}),
    () =>
      isForm
        ? request("/shops/feed", { method: "POST", body: formDataOrBody, auth: true })
        : apiPost("/shops/feed", formDataOrBody || {}),
    // optional legacy:
    () =>
      isForm
        ? request("/shop/posts", { method: "POST", body: formDataOrBody, auth: true })
        : apiPost("/shop/posts", formDataOrBody || {}),
  ]);
}

export async function editShopPost(postId, text) {
  if (!postId) throw new Error("postId is required");
  return tryPaths([
    () => apiPut(`/shop/feed/${postId}`, { text }),
    () => apiPut(`/shops/feed/${postId}`, { text }),
    // optional legacy:
    () => apiPut(`/shop/posts/${postId}`, { text }),
    () => apiPut(`/shops/posts/${postId}`, { text }),
  ]);
}

export async function deleteShopPost(postId) {
  if (!postId) throw new Error("postId is required");
  return tryPaths([
    () => apiDelete(`/shop/feed/${postId}`),
    () => apiDelete(`/shops/feed/${postId}`),
    // optional legacy:
    () => apiDelete(`/shop/posts/${postId}`),
    () => apiDelete(`/shops/posts/${postId}`),
  ]);
}

// ✅ Like / Unlike a shop feed post (SHOP side)
export async function toggleShopLike(postId) {
  const id = String(postId || "").trim();
  if (!id) throw new Error("Missing postId");

  // ✅ IMPORTANT: paths should NOT include /api (apiPost adds it automatically)
  // Also supports both possible backend patterns: /:id/like OR /posts/:id/like
  return await tryPaths([
    () => apiPost(`/shop/posts/${id}/like`, {}),
    () => apiPost(`/shop/feed/${id}/like`, {}),
    () => apiPost(`/shops/posts/${id}/like`, {}),
    () => apiPost(`/shops/feed/${id}/like`, {}),
  ]);
}


export async function addShopComment(postId, text) {
  if (!postId) throw new Error("postId is required");
  return tryPaths([
    () => apiPost(`/shop/feed/${postId}/comment`, { text: text || "" }),
    () => apiPost(`/shops/feed/${postId}/comment`, { text: text || "" }),
    // optional legacy:
    () => apiPost(`/shop/posts/${postId}/comment`, { text: text || "" }),
  ]);
}

export async function addShopReply(postId, commentId, text) {
  if (!postId) throw new Error("postId is required");
  if (!commentId) throw new Error("commentId is required");
  return tryPaths([
    () => apiPost(`/shop/feed/${postId}/comment/${commentId}/reply`, { text: text || "" }),
    () => apiPost(`/shops/feed/${postId}/comment/${commentId}/reply`, { text: text || "" }),
    // optional legacy:
    () => apiPost(`/shop/posts/${postId}/comment/${commentId}/reply`, { text: text || "" }),
  ]);
}

export async function toggleCommentLike(postId, commentId) {
  if (!postId) throw new Error("postId is required");
  if (!commentId) throw new Error("commentId is required");
  return tryPaths([
    () => apiPost(`/shop/feed/${postId}/comment/${commentId}/like`, {}),
    () => apiPost(`/shops/feed/${postId}/comment/${commentId}/like`, {}),
  ]);
}

export async function editComment(postId, commentId, text) {
  if (!postId) throw new Error("postId is required");
  if (!commentId) throw new Error("commentId is required");
  if (!text) throw new Error("text is required");
  return tryPaths([
    () => apiPut(`/shop/feed/${postId}/comment/${commentId}`, { text }),
    () => apiPut(`/shops/feed/${postId}/comment/${commentId}`, { text }),
  ]);
}

export async function deleteComment(postId, commentId) {
  if (!postId) throw new Error("postId is required");
  if (!commentId) throw new Error("commentId is required");
  return tryPaths([
    () => apiDelete(`/shop/feed/${postId}/comment/${commentId}`),
    () => apiDelete(`/shops/feed/${postId}/comment/${commentId}`),
  ]);
}

/* =========================
   ✅ FEATURED PRODUCTS
   GET  /api/shop/feed/featured
   PUT  /api/shop/feed/featured
   ========================= */
export async function getMyFeaturedProducts() {
  return tryPaths([
    () => apiGet("/shop/feed/featured"),
    () => apiGet("/shops/feed/featured"),
  ]);
}

export async function saveMyFeaturedProducts(featuredProductIds) {
  return tryPaths([
    () => apiPut("/shop/feed/featured", { featuredProductIds: featuredProductIds || [] }),
    () => apiPut("/shops/feed/featured", { featuredProductIds: featuredProductIds || [] }),
  ]);
}

/* =========================
   ✅ SHOP MALL (PRIVATE BUILDER)
   ✅ CANONICAL:
     GET  /api/shop-mall/me
     PUT  /api/shop-mall/me
     POST /api/shop-mall/me/cover
   ========================= */
export async function getMyShopMallPage() {
  const t = Date.now();
  return tryPaths([
    () => apiGet(`/shop-mall/me?t=${t}`),
    // optional legacy (remove if you don't have these in backend):
    () => apiGet(`/shop/mall/me?t=${t}`),
  ]);
}

export async function updateMyShopMallPage(mallPage) {
  console.log("🔧 API: updateMyShopMallPage called with:", mallPage);
  const result = await tryPaths([
    () => apiPut("/shop-mall/me", mallPage || {}),
    // optional legacy:
    () => apiPut("/shop/mall/me", mallPage || {}),
  ]);
  console.log("🔧 API: updateMyShopMallPage result:", result);
  return result;
}

export async function uploadShopMallCover(file) {
  if (!file) throw new Error("file is required");
  const fieldNames = ["file", "media", "image"];

  // try several field names to maximize compatibility with different backends
  let lastErr = null;
  for (const field of fieldNames) {
    try {
      const fd = new FormData();
      fd.append(field, file);

      return await tryPaths([
        () => request("/shop-mall/me/cover", { method: "POST", body: fd, auth: true }),
        // optional legacy:
        () => request("/shop/mall/me/cover", { method: "POST", body: fd, auth: true }),
      ]);
    } catch (e) {
      lastErr = e;
      // if 401 (no token) or other fatal, stop retrying
      if (e && Number(e.status) === 401) break;
      // otherwise try next field name
    }
  }

  // all attempts failed
  if (lastErr) throw lastErr;
  throw new Error("Upload failed");
}

export async function uploadShopMallAsset(file) {
  if (!file) throw new Error("File is required");
  const fd = new FormData();
  fd.append("file", file);
  return await request("/shop-mall/me/assets", { method: "POST", body: fd, auth: true });
}

/* =========================
   ✅ PUBLIC SHOP MALL (USED BY PREVIEW + USER APP)
   ✅ CANONICAL:
     GET /api/public/shops/:shopId/mall
   ========================= */
export async function getPublicShopMallPage(shopId) {
  if (!shopId) throw new Error("shopId is required");

  const sid = encodeURIComponent(String(shopId));
  const t = Date.now();

  return tryPaths([
    // ✅ BEST: your backend publicShopRoutes should serve this
    () => apiGet(`/public/shops/${sid}/mall?t=${t}`, { auth: false }),
  ]);
}

/* =========================
   ✅ Notifications (shop view)
   ========================= */
export async function getShopNotifications() {
  return tryPaths([
    () => apiGet("/shop/notifications"),
    () => apiGet("/shops/notifications"),
    () => apiGet("/notifications/shop"),
    () => apiGet("/notifications"),
  ]);
}

export async function markShopNotificationRead(notificationId) {
  if (!notificationId) throw new Error("notificationId is required");
  return tryPaths([
    () => apiPut(`/shop/notifications/${notificationId}/read`, {}),
    () => apiPut(`/shops/notifications/${notificationId}/read`, {}),
    () => apiPut(`/notifications/shop/${notificationId}/read`, {}),
    () => apiPut(`/notifications/${notificationId}/read`, {}),
  ]);
}

export async function markAllShopNotificationsRead() {
  return tryPaths([
    () => apiPut("/shop/notifications/read-all", {}),
    () => apiPut("/shops/notifications/read-all", {}),
    () => apiPut("/notifications/shop/read-all", {}),
    () => apiPut("/notifications/read-all", {}),
  ]);
}

export async function deleteShopNotification(notificationId) {
  if (!notificationId) throw new Error("notificationId is required");
  return tryPaths([
    () => apiDelete(`/shop/notifications/${notificationId}`),
    () => apiDelete(`/shops/notifications/${notificationId}`),
    () => apiDelete(`/notifications/${notificationId}`),
  ]);
}

export async function deleteAllShopNotifications() {
  return tryPaths([
    () => apiDelete("/shop/notifications"),
    () => apiDelete("/shops/notifications"),
    () => apiDelete("/notifications"),
  ]);
}

/* =========================
   ✅ Messages / Conversations
   ========================= */
export async function getShopConversations() {
  return apiGet("/messages/conversations");
}

export async function getShopConversationMessages(conversationId) {
  if (!conversationId) throw new Error("conversationId is required");
  return tryPaths([
    () => apiGet(`/messages/conversations/${conversationId}/messages`),
    // optional legacy:
    () => apiGet(`/messages/${conversationId}/messages`),
  ]);
}

export async function sendShopMessage(conversationId, text, extras = {}) {
  if (!conversationId) throw new Error("conversationId is required");

  const cleanText = String(text || "").trim();
  const isForm = typeof FormData !== "undefined" && extras instanceof FormData;

  if (isForm) {
    if (cleanText && !extras.get("text")) extras.append("text", cleanText);

    return tryPaths([
      () =>
        request(`/messages/conversations/${conversationId}/messages`, {
          method: "POST",
          body: extras,
          auth: true,
        }),
      // optional legacy:
      () =>
        request(`/messages/${conversationId}/messages`, {
          method: "POST",
          body: extras,
          auth: true,
        }),
    ]);
  }

  const payload = { text: cleanText, ...extras };
  if (!payload.text && !payload.image && !payload.video && !payload.attachments) {
    throw new Error("message is empty");
  }

  return tryPaths([
    () => apiPost(`/messages/conversations/${conversationId}/messages`, payload),
    // optional legacy:
    () => apiPost(`/messages/${conversationId}/messages`, payload),
  ]);
}

export async function createOrGetConversation(payload) {
  return apiPost("/messages/conversations", payload || {});
}

/* =========================
   ✅ Simplified messaging (for shops to users)
   ========================= */
export async function sendMessageToUser(userId, text) {
  if (!userId) throw new Error("userId is required");
  if (!text) throw new Error("text is required");
  
  return apiPost("/messages/send-to-user", { userId, text });
}

/* =========================
   ✅ Ask Previous Buyers Functions
   ========================= */
export async function startAskBuyerConversation({ toUserId, productId } = {}) {
  const u = String(toUserId || "").trim();
  const p = String(productId || "").trim();
  if (!u) throw new Error("Missing toUserId");
  if (!p) throw new Error("Missing productId");

  const data = await apiPost("/messages/conversations/start-ask-buyer", {
    toUserId: u,
    productId: p,
  });

  const id =
    data?.conversationId ||
    data?._id ||
    data?.conversation?._id ||
    data?.conversation?.id ||
    "";
  if (!id) throw new Error("Conversation not created (missing conversationId)");

  return { ...data, conversationId: String(id) };
}

export async function getAskBuyers(productId) {
  const pid = String(productId || "").trim();
  if (!pid) throw new Error("Missing productId");

  // Use the correct endpoint
  const res = await apiGet(`/products/${encodeURIComponent(pid)}/previous-buyers`);
  return res?.buyers || [];
}

/* =========================
   ✅ Reviews (public)
   ========================= */
export async function getProductReviewsPublic(productId) {
  if (!productId) throw new Error("productId is required");
  return tryPaths([
    () => apiGet(`/reviews/public/${productId}`, { auth: false }),
    // optional legacy:
    () => apiGet(`/reviews/${productId}/public`, { auth: false }),
    () => apiGet(`/reviews/product/${productId}`, { auth: false }),
  ]);
}

/* =========================
   ✅ Views Tracking
   ========================= */
export async function trackProductView(productId) {
  if (!productId) throw new Error("productId is required");
  // Deduplicate: skip if same product was counted within the last 30 min this session
  const key = `pv_${productId}`;
  try {
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < 30 * 60 * 1000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {}
  return tryPaths([
    () => apiPost(`/shop/views/${productId}`, {}, { auth: false }),
    () => apiPost(`/shops/views/${productId}`, {}, { auth: false }),
  ]);
}

/* =========================
   ✅ SHOP ANALYTICS
   ========================= */
export async function getShopAnalytics() {
  return tryPaths([
    () => apiGet("/shop/analytics"),
    () => apiGet("/shops/analytics"),
  ]);
}

export async function getShopAnalyticsSummary() {
  return tryPaths([
    () => apiGet("/shop/analytics/summary"),
    () => apiGet("/shops/analytics/summary"),
  ]);
}

export async function getShopViewsSummary() {
  return tryPaths([
    () => apiGet("/shop/views"),
    () => apiGet("/shops/views"),
  ]);
}

export async function getShopRevenueTrend(period = "7days") {
  return tryPaths([
    () => apiGet(`/shop/analytics/revenue?period=${period}`),
    () => apiGet(`/shops/analytics/revenue?period=${period}`),
  ]);
}

/* =========================
   ✅ LOW STOCK ALERTS
   ========================= */
export async function getLowStockProducts() {
  return tryPaths([
    () => apiGet("/shop/low-stock"),
    () => apiGet("/shops/low-stock"),
  ]);
}

export async function getLowStockCount() {
  return tryPaths([
    () => apiGet("/shop/low-stock/count"),
    () => apiGet("/shops/low-stock/count"),
  ]);
}

export async function updateLowStockThreshold(threshold) {
  return tryPaths([
    () => apiPut("/shop/low-stock/threshold", { threshold }),
    () => apiPut("/shops/low-stock/threshold", { threshold }),
  ]);
}

export async function restockProduct(productId, quantity) {
  if (!productId) throw new Error("productId is required");
  return tryPaths([
    () => apiPost(`/shop/low-stock/restock/${productId}`, { quantity }),
    () => apiPost(`/shops/low-stock/restock/${productId}`, { quantity }),
  ]);
}

/* =========================
   ✅ BULK PRODUCT UPLOAD
   ========================= */
export function getBulkUploadTemplateUrl() {
  return `${API_BASE}/api/shop/bulk-upload/template`;
}

export async function validateBulkUploadCSV(file) {
  const formData = new FormData();
  formData.append("csv", file);
  return tryPaths([
    () => request("/shop/bulk-upload/validate", { method: "POST", body: formData }),
    () => request("/shops/bulk-upload/validate", { method: "POST", body: formData }),
  ]);
}

export async function uploadBulkProducts(file) {
  const formData = new FormData();
  formData.append("csv", file);
  return tryPaths([
    () => request("/shop/bulk-upload", { method: "POST", body: formData }),
    () => request("/shops/bulk-upload", { method: "POST", body: formData }),
  ]);
}

/* =========================
   ✅ SHOP REVIEWS (Response System)
   ========================= */
export async function getShopReviews(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  
  return tryPaths([
    () => apiGet(`/shop/reviews${suffix}`),
    () => apiGet(`/shops/reviews${suffix}`),
  ]);
}

export async function getShopReviewStats() {
  return tryPaths([
    () => apiGet("/shop/reviews/stats"),
    () => apiGet("/shops/reviews/stats"),
  ]);
}

export async function respondToReview(reviewId, response) {
  if (!reviewId) throw new Error("reviewId is required");
  return tryPaths([
    () => apiPost(`/shop/reviews/${reviewId}/respond`, { response }),
    () => apiPost(`/shops/reviews/${reviewId}/respond`, { response }),
  ]);
}

export async function deleteReviewResponse(reviewId) {
  if (!reviewId) throw new Error("reviewId is required");
  return tryPaths([
    () => apiDelete(`/shop/reviews/${reviewId}/response`),
    () => apiDelete(`/shops/reviews/${reviewId}/response`),
  ]);
}

/* =========================
   ✅ SHOP PERFORMANCE SCORE
   ========================= */
export async function getShopPerformance() {
  return tryPaths([
    () => apiGet("/shop/performance"),
    () => apiGet("/shops/performance"),
  ]);
}

/* =========================
   ✅ STRIPE CONNECT
   ========================= */
export async function shopStripeCreateOrGetAccount(shopId) {
  if (!shopId) throw new Error("shopId is required");
  return apiPost("/stripe/connect/account", { shopId });
}

export async function shopStripeCreateOnboardingLink(shopId) {
  if (!shopId) throw new Error("shopId is required");
  return apiPost("/stripe/connect/onboarding-link", { shopId });
}

export async function shopStripeGetStatus(shopId) {
  if (!shopId) throw new Error("shopId is required");
  return apiGet(`/stripe/connect/status/${shopId}`);
}

/* =========================
   💰 PAYMENT SETTINGS (PayPal payout email + cash)
   ========================= */
export async function shopSavePaymentSettings(paymentSettings, currentPassword) {
  return apiPut("/shop-profile/me", { paymentSettings, ...(currentPassword ? { currentPassword } : {}) });
}

/* =========================
   🔗 SHOP PUBLIC URL
   ========================= */
export function getShopPublicUrl(shop) {
  const base = process.env.REACT_APP_USER_APP_URL || "https://moondala.one";
  const b = String(base).replace(/\/$/, "");
  // prefer username handle, fall back to shopSlug (legacy), then _id
  const handle = shop?.username || shop?.shopSlug;
  const id = shop?._id || shop?.id;
  return handle ? `${b}/shop/${handle}` : id ? `${b}/shop/${id}` : "";
}

/* =========================
   Health check
   ========================= */
export async function pingBackend() {
  return apiGet("/_whoami", { auth: false });
}

/* =========================
   Withdrawal (MSC)
   ========================= */
export async function getShopMscBalance() {
  return apiGet("/shop/withdrawal/msc-balance");
}

export async function createWithdrawalRequest(payload) {
  return apiPost("/shop/withdrawal/withdrawal-requests", payload);
}

export async function getShopWithdrawalRequests() {
  return apiGet("/shop/withdrawal/withdrawal-requests");
}

/**
 * Send MSC from the shop's balance to staff members (Moondala users) by username.
 * body: { recipients: [{ username, amountMsc }], note, requestId }
 * → { ok:true, results:[{ username, ok, status, transferId, error? }], newBalanceMsc }
 *   | { ok:false, code, error }
 * `requestId` makes the call idempotent: re-send the same id when retrying a
 * network failure so the server pays each recipient at most once.
 */
export async function sendShopStaffMsc({ recipients, note, requestId }) {
  return apiPost("/shop/msc/send-staff", { recipients, note, requestId });
}

/* =========================
   Shipping
   ========================= */
export async function getShippingStatus(orderId) {
  return apiGet(`/shipping/orders/${encodeURIComponent(orderId)}/status`);
}

export async function getLabelPreflight(orderId) {
  return tryPaths([
    () => apiGet(`/shipping/orders/${encodeURIComponent(orderId)}/label-preflight`),
    () => apiGet(`/shipping/label-preflight/${encodeURIComponent(orderId)}`),
  ]);
}

export async function createShippingLabel(orderId, payload = {}) {
  return tryPaths([
    () => apiPost(`/shipping/orders/${encodeURIComponent(orderId)}/create-label`, payload),
    () => apiPost(`/shipping/create-label/${encodeURIComponent(orderId)}`, payload),
  ]);
}

export async function getShippingRatesForOrder(orderId) {
  return apiGet(`/shipping/orders/${encodeURIComponent(orderId)}/status`);
}
